import { type Size } from '@volumental/common';
import {
  type Measurement,
  type RetailScan,
  type SizeLocale,
  type SizeRecommendation as GqlSizeRecommendation,
  type WebScan,
  type StyleMetaData,
  type FitScore,
} from '../api/gql/graphql';

export interface ScanListItem {
  type: 'WebScan' | 'RetailScan';
  id: string;
  createdAt: string;
  previousPurchaseID?: string;
}

export interface ScanResult {
  type: 'WebScan' | 'RetailScan';
  id: string;
  publicID: string;
  measurement: Measurement;
  meshes: {
    leftFoot: string;
    rightFoot: string;
  };
  ageGroup?: string;
  gender?: string;
  createdAt: string;
  claimed?: boolean;
}

export const toScanResult = (
  data: Partial<RetailScan> | Partial<WebScan> | undefined | null,
): ScanResult | undefined => {
  if (
    !data ||
    !data.measurement ||
    !data.__typename ||
    !data.id ||
    !data.publicID
  ) {
    return undefined;
  }
  return {
    type: data.__typename,
    id: data.id,
    publicID: data.publicID,
    measurement: data.measurement,
    meshes: data.meshes || { leftFoot: '', rightFoot: '' },
    ageGroup: (data.__typename === 'RetailScan' && data.ageGroup) || undefined,
    gender: (data.__typename === 'RetailScan' && data.gender) || undefined,
    createdAt: data.createdAt,
    claimed: data.__typename === 'WebScan' ? data.claimed : undefined,
  };
};

export interface SizeRecommendation {
  lengthSize: string;
  widthSize: string;
  predictorLevel: string;
  sizeLocale: SizeLocale;
  fitScores?: FitScore[];
}

export interface RecommendationError {
  code?: string;
  message: string;
}

export const toSizeRecommendation = (
  data: GqlSizeRecommendation | undefined | null,
): SizeRecommendation | undefined => {
  if (
    data?.lengthSize &&
    data.widthSize &&
    data.sizeLocale &&
    data.predictorLevel
  ) {
    return {
      lengthSize: data.lengthSize,
      widthSize: data.widthSize,
      sizeLocale: data.sizeLocale,
      predictorLevel: data.predictorLevel,
      fitScores: data.fitScores ?? undefined,
    };
  }
  return undefined;
};

export enum CurrentView {
  HistorySignIn = 'history-sign-in',
  HistorySelectScan = 'history-select-scan',
  HistoryValidateAccount = 'history-validate-account',
  Intro = 'intro',
  Picker = 'picker',
  SizeChart = 'sizeChart',
  SizePicker = 'size-picker',
  AddPPToScan = 'add-pp-to-scan',
  ScannerIntro = 'scanner-intro',
  ScannerPicker = 'scanner-picker',
  SoloOnboarding = 'solo-onboarding',
  AssistedOnboarding = 'assisted-onboarding',
  SoloCapturing = 'solo-capturing',
  AssistedCapturing = 'assisted-capturing',
  Reconstructing = 'reconstructing',
  ScannerDesktop = 'scanner-desktop',
  Result = 'result',
  RecomQRcodeView = 'recom-qrcode-view',
}

export enum AppMode {
  Recommendation = 'recommendation',
  Measurement = 'measurement',
  Sizechart = 'sizechart',
}

export enum CookieType {
  All = 'all',
  Essential = 'essential',
  Decline = 'decline',
}

export enum UserFlowType {
  Solo = 'solo',
  Assisted = 'assisted',
  Hybrid = 'hybrid',
  HistoryMScan = 'history_mobile_scan',
  HistoryRScan = 'history_retail_scan',
}

export const enum DisplayUnit {
  Centimeters = 'cm',
  Inches = '“',
}

export enum SoloOnboardingStage {
  RemoveShoes = 'removeShoes',
  RollPants = 'rollPants',
  PaperSizeSelect = 'paperSizeSelect',
  NextTakeShots = 'nextTakeShots',
  StandAsShown = 'standAsShown',
}
export enum AssistedOnboardingStage {
  HandOverPhone = 'handOverPhone',
  RemoveShoes = 'removeShoes',
  RollPants = 'rollPants',
  PaperSizeSelect = 'paperSizeSelect',
  NextTakeShots = 'nextTakeShots',
  StandAsShown = 'standAsShown',
  FirstCaptureInstruction = 'firstCaptureInstruction',
}

export enum UserError {
  NoPaper = 'noPaper',
  NoFeet = 'noFeet',
  UnrecognizablePaper = 'unrecognizablePaper',
  UnseenKeypoints = 'unseenKeypoints',
  Unknown = 'unknown',
}

export interface FitSurveyResult {
  gender_age: string;
  brand_name: string;
  style_name?: string;
  width?: number;
  length: number;
  size_locale: SizeLocale;
  widthLabel?: string;
  lengthLabel: string;
  brandLabel: string;
}

export interface GenderAgeChart {
  genderAge: string;
  sizes: Size[];
}

export interface SizeChartData {
  predictorLevel?: string;
  allCharts: GenderAgeChart[];
  shoe: StyleMetaData;
}
